// src/components/ChatInput.tsx
import { useState, useRef, useEffect, memo } from "react";
import { Button } from "@/components/ui/button";
import { Send, LogIn, Maximize2, Minimize2, Square } from 'lucide-react';
import type { UserState } from '@/types';
import type { SelectedItem } from '@/types/file-browser';
import type { MessageContext } from '@/types/chat';
import { FileBrowserButton } from '@/components/FileBrowserButton';
import { HistoryTemplatesDrawer } from '@/components/HistoryTemplatesDrawer';
import { InputArea } from '@/components/InputArea';
import VertexSearchWidget from '@/components/VertexAISearchWidget';
import { useMessageState } from '@/contexts/MessageStateContext';

interface MessageHistoryItem {
  text: string;
  timestamp: Date;
}

interface SavedTemplate {
  id: string;
  title: string;
  content: string;
}

interface ChatInputProps {
  input: string;
  botName: string;
  senderName: string;
  userState: UserState;
  isStreaming: boolean;
  onInputChange: (value: string) => void;
  onSendMessage: (messageContext: MessageContext) => void;
  onLogin: () => void;
  tools?: string[];  
  toolConfigs?: Record<string, Record<string, any>>;
  // Optional history limit
  messageHistoryLimit?: number;
  // Optional saved templates
  savedTemplates?: SavedTemplate[];
  // Optional callback for saving a new template
  onSaveTemplate?: (title: string, content: string) => void;
  onStopStreaming: () => void; // Add this new prop

}

export const ChatInput = memo(function ChatInput({
  input,
  botName,
  senderName,
  userState,
  isStreaming,
  onInputChange,
  onSendMessage,
  onLogin,
  tools = [],
  toolConfigs = {},
  messageHistoryLimit = 50,
  savedTemplates = [],
  onSaveTemplate = () => {}, 
  onStopStreaming = () => {},
}: ChatInputProps) {
  // Get selectedItems from context instead of props
  const { selectedItems, setSelectedItems } = useMessageState();
  // Load message history from localStorage on initial render
  const [messageHistory, setMessageHistory] = useState<MessageHistoryItem[]>(() => {
    try {
      const savedHistory = localStorage.getItem('chatMessageHistory');
      return savedHistory ? JSON.parse(savedHistory) : [];
    } catch (error) {
      console.error('Failed to load message history:', error);
      return [];
    }
  });
  
  // Load saved templates from localStorage if not provided as props
  const [localTemplates, setLocalTemplates] = useState<SavedTemplate[]>(() => {
    if (savedTemplates.length > 0) return []; // Don't initialize local templates if provided via props
    
    try {
      const savedLocalTemplates = localStorage.getItem('chatResponseTemplates');
      return savedLocalTemplates ? JSON.parse(savedLocalTemplates) : [];
    } catch (error) {
      console.error('Failed to load templates:', error);
      return [];
    }
  });
  
  // Use either prop templates or local templates
  const templates = savedTemplates.length > 0 ? savedTemplates : localTemplates;
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const showFileBrowser = tools.includes('file-browser') || tools.includes('document_search_agent');
  const showVertexSearch = tools.includes('vertex_search_app');
  const vertexSearchConfig = toolConfigs.vertex_search_app || {};
  const canSaveToServer = savedTemplates.length > 0;
  
  // Check if device is mobile on mount and on window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // Standard mobile breakpoint
    };
    
    // Initial check
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Auto-collapse when streaming starts on mobile
  useEffect(() => {
    if (isStreaming && isMobile) {
      setIsCollapsed(true);
    } else if (!isStreaming && isCollapsed) {
      // Automatically expand when streaming ends, but only if it was auto-collapsed
      setIsCollapsed(false);
    }
  }, [isStreaming, isMobile, isCollapsed]);
  
  // Save message history to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('chatMessageHistory', JSON.stringify(messageHistory));
    } catch (error) {
      console.error('Failed to save message history:', error);
    }
  }, [messageHistory]);
  
  // Save templates to localStorage whenever they change (only if using local templates)
  useEffect(() => {
    if (savedTemplates.length === 0 && localTemplates.length > 0) {
      try {
        localStorage.setItem('chatResponseTemplates', JSON.stringify(localTemplates));
      } catch (error) {
        console.error('Failed to save templates:', error);
      }
    }
  }, [localTemplates, savedTemplates.length]);

  const handleSend = () => {
    if (input.trim() || selectedItems.length > 0) {
      // Check for form data to prepend
      const formData = localStorage.getItem('dynamicui_form_data');
      let messageText = input;
      
      if (formData) {
        messageText = formData + input;
        // Clear the form data after using it
        localStorage.removeItem('dynamicui_form_data');
      }
      
      // Add to message history if there's text content
      if (input.trim()) {
        setMessageHistory(prev => {
          const newHistory = [...prev, { 
            text: input.trim(), 
            timestamp: new Date() 
          }];
          
          // Limit history size
          if (newHistory.length > messageHistoryLimit) {
            return newHistory.slice(-messageHistoryLimit);
          }
          return newHistory;
        });
      }
      
      onSendMessage({
        text: messageText,
        selectedItems: selectedItems
      });
      
      // Close drawer if open when sending
      setHistoryDrawerOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const applyHistoryMessage = (message: string) => {
    onInputChange(message);
  };

  const applyTemplate = (template: string) => {
    onInputChange(template);
  };

  const handleSaveTemplate = (title: string, content: string) => {
    if (content.trim() && title.trim()) {
      const newTemplate: SavedTemplate = {
        id: `template-${Date.now()}`,
        title: title.trim(),
        content: content.trim()
      };
      
      // If using props-based templates, use the callback
      if (savedTemplates.length > 0) {
        onSaveTemplate(title.trim(), content.trim());
      } 
      // Otherwise use local storage
      else {
        setLocalTemplates(prev => [...prev, newTemplate]);
      }
    }
  };

  // Function to delete a template
  const deleteTemplate = (templateId: string) => {
    if (savedTemplates.length > 0) {
      // If using props-based templates, would need an additional callback here
      console.warn("Deleting prop-based templates not implemented");
    } else {
      setLocalTemplates(prev => prev.filter(template => template.id !== templateId));
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  
  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Simplified placeholder text since we're always in bot mode
  const placeholderText = `Send message to ${botName}...`;

  return (
    <div 
      ref={containerRef}
      className={`transition-all duration-200 border-t ${isExpanded ? 'sticky bottom-0 bg-background shadow-lg z-10' : ''} ${isCollapsed ? 'h-12 overflow-hidden' : ''}`}
    >
      {isCollapsed ? (
        // Collapsed view - just a small bar with expand button
        <div className="flex items-center justify-between px-4 py-2 h-12">
          <div className="text-sm text-gray-500 truncate flex-1">
            {input.trim() ? 
              (input.length > 30 ? `${input.substring(0, 30)}...` : input) : 
              `Send message to ${botName}...`}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleCollapsed}
            className="ml-2"
          >
            Show
          </Button>
        </div>
      ) : (
        // Expanded view - compact input bar
        <div className="px-3 py-2">
          <div className="flex space-x-2 items-center">
            {/* Left side tools */}
            <div className="flex space-x-1">
              {/* Drawer for history and templates */}
              <HistoryTemplatesDrawer
                messageHistory={messageHistory}
                templates={templates}
                currentInputValue={input}
                canSaveToServer={canSaveToServer}
                onApplyHistoryMessage={applyHistoryMessage}
                onApplyTemplate={applyTemplate}
                onSaveTemplate={handleSaveTemplate}
                onDeleteTemplate={deleteTemplate}
                onDrawerOpenChange={setHistoryDrawerOpen}
                isDrawerOpen={historyDrawerOpen}
              />
              
              {/* File browser button */}
              {showFileBrowser && (
                <FileBrowserButton
                  onItemsSelected={setSelectedItems}
                  selectedItems={selectedItems}
                />
              )}
              
              {/* Vertex Search Widget as an icon button */}
              {showVertexSearch && userState !== 'not-logged-in' && (
                <VertexSearchWidget
                  configId={vertexSearchConfig.configId || "c09df02f-06e3-4285-b62f-823dcb95407d"}
                  iconOnly={true}
                />
              )}
            </div>

            {/* Chat input field */}
            <InputArea
              input={input}
              isStreaming={isStreaming}
              isExpanded={isExpanded}
              placeholder={placeholderText}
              onInputChange={onInputChange}
              onKeyDown={handleKeyDown}
            />
            
            {/* Right side buttons */}
            <div className="flex space-x-1">
              {/* Expand/collapse textarea button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleExpanded}
                className="h-10 w-10"
                title={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded 
                  ? <Minimize2 className="h-4 w-4" /> 
                  : <Maximize2 className="h-4 w-4" />
                }
              </Button>

              {/* Hide button (mobile only) */}
              {isMobile && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleCollapsed}
                  className="h-10 w-10 ml-1"
                  title="Hide input"
                >
                  <Minimize2 className="h-4 w-4" />
                </Button>
              )}
              
              {/* Send/Stop button */}
              {isStreaming ? (
                <Button 
                  onClick={onStopStreaming}
                  variant="destructive"
                  className="h-10 w-10"
                  title="Stop response"
                >
                  <Square className="h-4 w-4" /> 
                </Button>
              ) : (
                <Button 
                  onClick={handleSend}
                  disabled={(!input.trim() && selectedItems.length === 0)}
                  className="h-10 w-10"
                  title="Send message"
                >
                  <Send className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          
          {/* Helper text (only visible when not expanded) */}
          {!isExpanded && !isMobile && (
            <div className="text-[10px] text-gray-400 mt-1 px-2">
              Shift+Enter for new line. This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy">Privacy Policy</a> and <a href="https://policies.google.com/terms">Terms of Service</a> apply.
            </div>
          )}
        </div>
      )}
    </div>
  );
});